import React from 'react';
import {Box, ButtonGroup, Grid} from '@mui/material';
import {UpdateVendorDto} from 'pwm-shared/dist';
import {Vendor} from 'pwm-shared';
import {IVendorDbaEntity} from 'pwm-shared/src/types/Vendor';
import {plainToInstance} from 'class-transformer';
import {useVendorApi} from '../vendor.api';
import {useToast} from 'src/PWM/hooks/use-toast';
import {useTranslation} from 'react-i18next';
import {tokens} from 'src/PWM/locales/tokens';
import PWMTextInput from 'src/PWM/components/form/PWMTextInput';
import {ButtonDefault, ButtonSecondary} from 'src/PWM/components/PWMButtons';
import {BodyText} from 'src/PWM/components/PWMTypography';
import PWMCheckboxControl from 'src/PWM/components/form/PWMCheckboxControl';
import EditDbaNames from './EditDbaNames';
import ConfirmationDialog from 'src/PWM/components/ConfirmationDialog';

const VendorEditInfoComponent: React.FC<IVendorEditInfoComponentProps> = (props) => {
  const [isConfirmCancelDialogOpen, setIsConfirmCancelDialogOpen] = React.useState<boolean>(false);
  const [vendorInfo, setVendorInfo] = React.useState<UpdateVendorDto>(new UpdateVendorDto());
  const [isBlocking, setIsBlocking] = React.useState<boolean>(false);

  const {data, done} = props;
  const vendorApi = useVendorApi();
  const toast = useToast();
  const {t} = useTranslation();

  React.useEffect(() => {
    const vendorInfoDto = plainToInstance(UpdateVendorDto, data);
    setVendorInfo(vendorInfoDto);
    setIsBlocking(false);
  }, [data]);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();
    if (vendorApi) {
      try {
        await vendorApi.updateVendor(vendorInfo);
        toast.success(`${t(tokens.interface.saved)}`);
        done(true);
      } catch (err) {
        console.error(err);
      } finally {
        setIsBlocking(false);
      }
    }
  };

  /**
   * Handles the cancellation of the edit action.
   * If there is a blocking operation in progress, it prompts for confirmation before canceling.
   *
   * @function onCancelEdit
   * @returns {void}
   */
  const onCancelEdit = () => {
    if(isBlocking && window.confirm(`${t(tokens.interface.confirmcancel)}`)){
      done(false);
    }
    done(isBlocking);
  }

  const onInputChange = (e: any) => {
    setIsBlocking(true);
    // @ts-ignore
    const newState: UpdateVendorDto = {
      ...vendorInfo
    };
    var value = e.target.value;
    if(e.target.type === 'checkbox'){
      value = e.target.checked;
    }
    // @ts-ignore
    newState[e.target.name] = value;
    setVendorInfo(newState);
  };

  const onUpdateDbaNames = async (dbaNames: IVendorDbaEntity[]) => {
    toast.success(`${t(tokens.interface.saved)}`);
    props.done(true);
  }

  const activeMessage = vendorInfo.isActive ? t(tokens.interface.activemessage) : t(tokens.interface.inactivemessage);

  return (
    <>
      <Box component="form" onSubmit={handleFormSubmit}>
        <Grid container={true} spacing={2} justifyContent="flex-start">
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="name"
              label={`${t(tokens.interface.dirregname)}`}
              value={vendorInfo.name}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={12}>
            <EditDbaNames vendorInfo={props.data as unknown as IVendorDbaEntity} onSave={onUpdateDbaNames} />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="emailAddress"
              label={`${t(tokens.interface.contactemail)}`}
              value={vendorInfo.emailAddress}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="address1"
              label={`${t(tokens.interface.address1)}`}
              value={vendorInfo.address1}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="address2"
              label={`${t(tokens.interface.address2)}`}
              value={vendorInfo.address2}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="address3"
              label={`${t(tokens.interface.address3)}`}
              value={vendorInfo.address3}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="city"
              label={`${t(tokens.interface.city)}`}
              value={vendorInfo.city}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="state"
              label={`${t(tokens.interface.state)}`}
              value={vendorInfo.state}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="zip"
              label={`${t(tokens.interface.zip)}`}
              value={vendorInfo.zip}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="dirRegNo"
              label="DIR #"
              value={vendorInfo.dirRegNo}
              onChange={onInputChange}
              fullWidth={true}
              required={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMTextInput
              id="licNo"
              label={`${t(tokens.interface.licno)}`}
              value={vendorInfo.licNo}
              onChange={onInputChange}
              fullWidth={true}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <PWMCheckboxControl
              name="isActive"
              label={`${t(tokens.interface.vendor)} ${t(tokens.interface.active)}`}
              isChecked={vendorInfo.isActive}
              onChange={onInputChange}
            />
            <div style={{display: 'block'}}>
              <BodyText>{activeMessage}</BodyText>
            </div>
          </Grid>
          <Grid item={true} xs={12}>
            <ButtonGroup>
              <ButtonSecondary type="submit">{t(tokens.interface.save)}</ButtonSecondary>
              <ButtonDefault type="reset" onClick={onCancelEdit}>{t(tokens.interface.cancel)}</ButtonDefault>
            </ButtonGroup>
          </Grid>
        </Grid>
      </Box>
      <ConfirmationDialog
        isOpen={isConfirmCancelDialogOpen}
        message={`${t(tokens.interface.confirmcancel)}`}
        onClose={() => setIsConfirmCancelDialogOpen(false)}
        closeButtonText={`${t(tokens.interface.cancel)}`}
        onOk={() => props.done(false)}
        okButtonText={`${t(tokens.interface.discardchanges)}`}
        title={`${t(tokens.interface.cancel)}`}
      />
    </>
  );
};

export default VendorEditInfoComponent;

interface IVendorEditInfoComponentProps {
  children?: React.ReactNode;
  data: Vendor.ProjectVendorListInfo;
  done(didUpdate: boolean): any;
}
